// Grid density
// ----------------------------------------------------------------------
// One padding scale shared by the head and the body of a grid. Before this,
// body cells carried an inline padding of 8px while the head kept MUI's 16px
// default, so the header sat on a different vertical rhythm than the rows it
// labelled.
//
// Density is a single user-wide preference rather than a per-grid one: someone
// who wants to see more rows wants that everywhere, and it would be strange for
// two grids in the same app to disagree about row height.

export const DENSITY_STORAGE_KEY = 'gridDensity';

export const DEFAULT_DENSITY = 'compact';

// Density sets type size as well as padding: at compact, 14px body text in a
// 31px row leaves almost no breathing room, and the point of compact is to fit
// more rows on screen rather than to crowd the same text.
//
// `rowHeight` is padding on both sides plus one line of that type. It is the
// figure loading skeletons size themselves against, so a skeleton row and a real
// row occupy the same space and content doesn't jump on load.
const row = ({ paddingY, fontSize, lineHeight }) => ({
    paddingY,
    fontSize,
    lineHeight,
    rowHeight: paddingY * 2 + Math.round(fontSize * lineHeight)
});

export const DENSITIES = {
    compact: { paddingX: 10, ...row({ paddingY: 6, fontSize: 13, lineHeight: 1.45 }) },
    standard: { paddingX: 12, ...row({ paddingY: 10, fontSize: 14, lineHeight: 1.5 }) },
    comfortable: {
        paddingX: 16,
        ...row({ paddingY: 14, fontSize: 14, lineHeight: 1.57 })
    }
};

export const DENSITY_ORDER = ['compact', 'standard', 'comfortable'];

export const getDensity = (density) =>
    DENSITIES[density] || DENSITIES[DEFAULT_DENSITY];

export const readDensity = () => {
    try {
        const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
        return DENSITIES[stored] ? stored : DEFAULT_DENSITY;
    } catch (e) {
        // Private-mode Safari throws on localStorage access.
        return DEFAULT_DENSITY;
    }
};

export const writeDensity = (density) => {
    try {
        if (DENSITIES[density]) {
            localStorage.setItem(DENSITY_STORAGE_KEY, density);
        }
    } catch (e) {
        // Preference simply won't survive a reload; not worth surfacing.
    }
};

/**
 * sx for a <Table> that applies the density to every cell it contains, head and
 * body alike. Checkbox and menu cells keep their own tighter padding, so they
 * are left out.
 */
export const densityTableSx = (density) => {
    const { paddingY, paddingX, fontSize, lineHeight } = getDensity(density);
    return {
        '& .MuiTableCell-root': {
            fontSize: `${fontSize}px`,
            lineHeight
        },
        '& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)': {
            paddingTop: `${paddingY}px`,
            paddingBottom: `${paddingY}px`,
            paddingLeft: `${paddingX}px`,
            paddingRight: `${paddingX}px`
        },
        // Cells whose content is a Typography (item lists, combined values)
        // would otherwise keep the component's own size and ignore density.
        '& .MuiTableCell-root .MuiTypography-body1, & .MuiTableCell-root .MuiTypography-body2': {
            fontSize: 'inherit',
            lineHeight: 'inherit'
        },
        // Caption stays one step down — it marks secondary detail inside a cell,
        // and that relationship should hold at every density.
        '& .MuiTableCell-root .MuiTypography-caption': {
            fontSize: `${Math.max(11, fontSize - 1)}px`,
            lineHeight
        }
    };
};
